import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { zustandPersistStorage } from '../appState/zustandPersistStorage'

export type ScannerType = 'RFD8500' | 'RFD40'

export type DeviceConnected = { rfid_device: string }

export type ScannerNativeDiagnostics = Record<string, unknown>

/** off = none · pending = machine selected / HID armed · live = physical tag received */
export type ScannerLinkState = 'off' | 'pending' | 'live'

type RfidSessionState = {
  docType: 'kit' | 'non-kit'
  scannerType: ScannerType | null
  /** True only after a physical (or mock) tag proves the link. */
  scannerConnected: boolean
  /** Machine chosen and capture armed, waiting for first tag. */
  scannerPending: boolean
  deviceConnected: DeviceConnected | null
  scannerBatteryPercent: number | null
  lastScannerConnectAt: string | null
  nativeDiagnostics: ScannerNativeDiagnostics | null
  hasSkippedScanner: boolean
  setScannerType: (type: ScannerType | null) => void
  setScannerConnected: (connected: boolean) => void
  setDeviceConnected: (device: DeviceConnected | null) => void
  setScannerBatteryPercent: (pct: number | null) => void
  setNativeDiagnostics: (diag: ScannerNativeDiagnostics | null) => void
  setHasSkippedScanner: (skipped: boolean) => void
  setDocType: (docType: 'kit' | 'non-kit') => void
  /** Machine selected + capture armed — NOT a verified Bluetooth link yet. */
  applySelection: (
    type: ScannerType,
    device: DeviceConnected,
    batteryPercent?: number | null
  ) => void
  /** Alias kept for older call sites — same as applySelection. */
  applyConnection: (
    type: ScannerType,
    device: DeviceConnected,
    batteryPercent?: number | null
  ) => void
  /** First real tag arrived — mark link live. */
  confirmScannerLink: () => void
  clearConnection: () => void
  prepareScannerGreeting: () => void
  getLinkState: () => ScannerLinkState
}

export const useRfidSessionStore = create<RfidSessionState>()(
  persist(
    (set, get) => ({
      docType: 'kit',
      scannerType: null,
      scannerConnected: false,
      scannerPending: false,
      deviceConnected: null,
      scannerBatteryPercent: null,
      lastScannerConnectAt: null,
      nativeDiagnostics: null,
      hasSkippedScanner: false,

      setScannerType: (scannerType) => set({ scannerType }),
      setScannerConnected: (scannerConnected) => set({ scannerConnected }),
      setDeviceConnected: (deviceConnected) => set({ deviceConnected }),
      setScannerBatteryPercent: (scannerBatteryPercent) =>
        set({ scannerBatteryPercent }),
      setNativeDiagnostics: (nativeDiagnostics) => set({ nativeDiagnostics }),
      setHasSkippedScanner: (hasSkippedScanner) => set({ hasSkippedScanner }),
      setDocType: (docType) => set({ docType }),

      applySelection: (type, device, batteryPercent) =>
        set({
          scannerType: type,
          scannerConnected: false,
          scannerPending: true,
          deviceConnected: device,
          scannerBatteryPercent: batteryPercent ?? null,
          lastScannerConnectAt: new Date().toISOString(),
          hasSkippedScanner: false,
        }),

      applyConnection: (type, device, batteryPercent) =>
        get().applySelection(type, device, batteryPercent),

      confirmScannerLink: () => {
        const s = get()
        if (!s.deviceConnected) return
        if (s.scannerConnected) return
        set({
          scannerConnected: true,
          scannerPending: false,
          lastScannerConnectAt: new Date().toISOString(),
        })
      },

      clearConnection: () =>
        set({
          scannerConnected: false,
          scannerPending: false,
          deviceConnected: null,
          scannerBatteryPercent: null,
          nativeDiagnostics: null,
        }),

      prepareScannerGreeting: () => set({ hasSkippedScanner: false }),

      getLinkState: () => {
        const s = get()
        if (s.scannerConnected) return 'live'
        if (s.scannerPending && s.deviceConnected) return 'pending'
        return 'off'
      },
    }),
    {
      name: 'rfid-session-storage',
      storage: createJSONStorage(() => zustandPersistStorage),
      partialize: (s) => ({
        docType: s.docType,
        scannerType: s.scannerType,
        // Never persist a fake "connected" — require a fresh verify after reload
        scannerConnected: false,
        scannerPending: false,
        deviceConnected: null,
        scannerBatteryPercent: null,
        hasSkippedScanner: s.hasSkippedScanner,
      }),
    }
  )
)

/** Capture should run while pending verify or fully live. */
export function isScannerSessionActive(s: {
  scannerConnected: boolean
  scannerPending: boolean
}): boolean {
  return s.scannerConnected || s.scannerPending
}
